// SEO Configuration for Airport Parking Deals
export const defaultSEO = {
  // Basic SEO
  title: "Compare Airport Parking Deals UK | Save on All Major Airports - Airport Parking Deals",
  titleTemplate: "%s - Airport Parking Deals",
  description: "Compare and book airport parking across all major UK airports. Save up to 60% with secure, reliable parking options including meet & greet, park & ride, and long-term parking.",
  keywords: "airport parking UK, cheap airport parking, Heathrow parking, Gatwick parking, meet and greet parking, park and ride, airport parking comparison, secure airport parking, long-term parking UK",

  // Site Information
  siteName: "Airport Parking Deals",
  siteUrl: "https://airportparkingdeals.co.uk",
  author: "Airport Parking Deals",
  language: "en",
  locale: "en_GB",

  // Social Media
  twitterHandle: "@airportparkingdeals",
  facebookAppId: "",

  // Images
  defaultImage: "/images/airport-parking-deals-og-image.jpg",
  favicon: "/favicon.ico",

  // Business Information
  businessType: "TravelAgency",
  contactInfo: {
    telephone: "00000000000",
    email: "info@dummy.co.uk",
    address: {
      streetAddress: "123 Parking Street",
      addressLocality: "London",
      addressRegion: "Greater London",
      postalCode: "SW1A 1AA",
      addressCountry: "GB"
    }
  },

  // Operating Hours
  operatingHours: {
    opens: "00:00",
    closes: "23:59",
    dayOfWeek: [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday"
    ]
  }
};

// Page-specific SEO configurations
export const pageSEO = {
  home: {
    title: "Compare Airport Parking Deals UK | Save on Heathrow, Gatwick & More",
    description: "Find and compare airport parking across all major UK airports. Save up to 60% with secure, reliable parking options including meet & greet, park & ride, and long-term parking.",
    keywords: "airport parking comparison UK, cheap airport parking UK, Heathrow parking deals, Gatwick parking comparison, London airport parking, meet & greet parking UK, park & ride airport UK, airport parking discount code, airport parking promo code",
    path: "/"
  },

  search: {
    title: "Search Airport Parking Deals | Compare Prices & Book Online",
    description: "Search and compare airport parking options across UK airports. Find the best deals on meet & greet, park & ride, and long-term parking with instant booking confirmation.",
    keywords: "search airport parking, compare parking prices, book airport parking online, UK airport parking search, parking deals comparison",
    path: "/search"
  },

  booking: {
    title: "Book Airport Parking | Secure Online Booking | Park Plus",
    description: "Complete your airport parking booking with our secure online system. Get instant confirmation and save up to 60% on your parking costs.",
    keywords: "book airport parking, secure parking booking, online parking reservation, airport parking confirmation",
    path: "/booking"
  },

  contact: {
    title: "Contact Us | Airport Parking Deals Customer Support",
    description: "Need help with your airport parking booking? Contact Airport Parking Deals customer support for assistance with reservations, modifications, or general inquiries.",
    keywords: "contact airport parking deals, customer support, parking help, booking assistance",
    path: "/contact-us"
  },

  privacy: {
    title: "Privacy Policy | Airport Parking Deals Data Protection",
    description: "Learn how Airport Parking Deals protects your personal information and data privacy when you book airport parking through our platform.",
    keywords: "privacy policy, data protection, personal information, GDPR compliance",
    path: "/privacy-policy"
  },

  terms: {
    title: "Terms and Conditions | Airport Parking Deals Booking Terms",
    description: "Read Airport Parking Deals terms and conditions for airport parking bookings, including cancellation policies, refund terms, and service conditions.",
    keywords: "terms and conditions, booking terms, cancellation policy, service terms",
    path: "/terms-and-conditions"
  },

  faqs: {
    title: "Frequently Asked Questions | Airport Parking Deals Help Center",
    description: "Find answers to common questions about airport parking bookings, payment methods, cancellations, and more in our comprehensive FAQ section.",
    keywords: "FAQ, frequently asked questions, parking help, booking questions, customer support",
    path: "/faqs"
  }
};

// Structured Data Templates
export const structuredDataTemplates = {
  organization: {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Airport Parking Deals",
    "description": "UK's leading airport parking comparison and booking platform",
    "url": "https://airportparkingdeals.co.uk",
    "logo": {
      "@type": "ImageObject",
      "url": "https://airportparkingdeals.co.uk/logo.png",
      "width": 512,
      "height": 512
    },
    "image": "https://airportparkingdeals.co.uk/logo.png",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "00000000000",
      "contactType": "customer service",
      "areaServed": "GB",
      "availableLanguage": "English"
    },
    "sameAs": [
      "https://facebook.com/airportparkingdeals",
      "https://twitter.com/airportparkingdeals",
      "https://linkedin.com/company/airportparkingdeals"
    ]
  },

  service: {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Airport Parking Comparison and Booking",
    "description": "Compare and book secure airport parking across all major UK airports with savings up to 60%",
    "provider": {
      "@type": "Organization",
      "name": "Airport Parking Deals"
    },
    "areaServed": {
      "@type": "Country",
      "name": "United Kingdom"
    },
    "serviceType": "Airport Parking Services"
  },

  website: {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Airport Parking Deals",
    "url": "https://airportparkingdeals.co.uk",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://airportparkingdeals.co.uk/search?airport={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  }

};

// Airport-specific SEO data
export const airportSEO = {
  heathrow: {
    title: "Heathrow Airport Parking | Compare Deals & Save Up to 60%",
    description: "Find the best Heathrow airport parking deals. Compare meet & greet, park & ride, and long-term parking options with instant booking and guaranteed spaces.",
    keywords: "Heathrow parking, LHR parking, Heathrow meet and greet, Heathrow long term parking, cheap Heathrow parking, Heathrow Terminal 1 parking, Heathrow Terminal 2 parking, Heathrow Terminal 3 parking, Heathrow Terminal 4 parking, Heathrow Terminal 5 parking",
    path: "/heathrow-airport-parking"
  },
  gatwick: {
    title: "Gatwick Airport Parking | Compare Meet & Greet, Park & Ride & Official Parking",
    description: "Book Gatwick Airport parking at the best prices with AirportParkingDeals.co.uk. Compare official & off-airport options, meet & greet, park & ride with trusted providers.",
    keywords: "Gatwick Airport Parking, Gatwick Meet and Greet, Gatwick Park and Ride, Gatwick North Terminal Parking, Gatwick South Terminal Parking, Cheap Gatwick Parking, Official Gatwick Parking, Off-site Gatwick Parking",
    path: "/gatwick-airport-parking"
  },
  manchester: {
    title: "Manchester Airport Parking | Meet & Greet, Park & Ride & Official Parking",
    description: "Book Manchester Airport parking at the best prices. Compare official, off-airport, meet & greet, and park & ride options at AirportParkingDeals.co.uk",
    keywords: "Manchester Airport Parking, Manchester Meet & Greet, Manchester Park & Ride, Manchester Official Parking, Terminal Parking",
    path: "/manchester-airport-parking"
  },
  stansted: {
    title: "Stansted Airport Parking | Meet & Greet, Park & Ride & Official Parking",
    description: "Compare and book Stansted Airport parking at AirportParkingDeals.co.uk. Find secure official, off-airport, meet & greet, and park & ride options at the best prices.",
    keywords: "Stansted Airport Parking, Stansted Meet & Greet, Stansted Park & Ride, Stansted Official Parking, Stansted Terminal Parking, Cheap Stansted Parking",
    path: "/stansted-airport-parking"
  },
  bristol: {
    title: "Bristol Airport Parking | Park & Ride, Meet & Greet & Official Parking",
    description: "Find and compare Bristol Airport parking. Choose from official, off-airport, meet & greet, and park & ride options at AirportParkingDeals.co.uk.",
    keywords: "Bristol Airport Parking, Bristol Meet & Greet, Bristol Park & Ride, Bristol Official Parking, Bristol Terminal Parking",
    path: "/bristol-airport-parking"
  },
  luton: {
    title: "Luton Airport Parking | Park & Ride, Meet & Greet & Official Parking",
    description: "Compare and book Luton Airport parking with AirportParkingDeals.co.uk. Find official, off-airport, meet & greet, and park & ride options at best prices",
    keywords: "Luton Airport Parking, Luton Meet & Greet, Luton Park & Ride, Luton Official Parking, Luton Terminal Parking",
    path: "/luton-airport-parking"
  },
  birmingham: {
    title: "Birmingham Airport Parking | Meet & Greet, Park & Ride & Official Parking.",
    description: "Book Birmingham Airport parking with AirportParkingDeals.co.uk. Compare official, off-airport, meet & greet, and park & ride options at best rates.",
    keywords: "Birmingham Airport Parking, Birmingham Meet & Greet, Birmingham Park & Ride, Birmingham Official Parking, Birmingham Terminal Parking.",
    path: "/birmingham-airport-parking"
  }
};

